import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Calendar, Clock, User, Tag, ArrowLeft, ArrowRight, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import AnimatedLogo from "../components/AnimatedLogo";
import { API } from "../App";
import { optimizedImageUrl } from "../lib/imageUrl";

// Table of Contents component
const TableOfContents = ({ content }) => {
  const [headings, setHeadings] = useState([]);

  useEffect(() => {
    // Extract H2 and H3 headings from markdown
    const matches = content.match(/^#{2,3}\s+.+$/gm) || [];
    const toc = matches.map((heading, index) => {
      const level = heading.startsWith("### ") ? 3 : 2;
      const text = heading.replace(/^#{2,3}\s+/, "");
      const id = text.toLowerCase().replace(/[^\w]+/g, "-");
      return { id, text, level };
    });
    setHeadings(toc);
  }, [content]);

  if (headings.length < 3) return null;

  return (
    <nav className="card-royal p-6 mb-8">
      <h3 className="font-cinzel font-bold text-[#D4AF37] mb-4">Table of Contents</h3>
      <ul className="space-y-2">
        {headings.map((h, i) => (
          <li key={i} className={h.level === 3 ? "ml-4" : ""}>
            <a
              href={`#${h.id}`}
              className="font-manrope text-[#94A3B8] hover:text-[#D4AF37] transition-colors text-sm"
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// Custom heading renderer that adds IDs
const HeadingRenderer = ({ level, children }) => {
  const text = children?.toString() || "";
  const id = text.toLowerCase().replace(/[^\w]+/g, "-");
  const Tag = `h${level}`;
  const styles = {
    2: "font-cinzel text-2xl font-bold text-[#F8FAFC] mt-10 mb-4",
    3: "font-cinzel text-xl font-bold text-[#D4AF37] mt-8 mb-3",
    4: "font-manrope text-lg font-semibold text-[#94A3B8] mt-6 mb-2",
  };
  return <Tag id={id} className={styles[level] || ""}>{children}</Tag>;
};

// prerender-seo.js bakes the same data used to render the static article
// into window.__PRERENDERED_POST__ so it's available synchronously before
// this component's first render. Without this, the initial render always
// showed a spinner (see below) regardless of what was already painted in
// the static HTML - React wiped it out before the fetch even started, and
// LCP excludes elements removed from the DOM, so the baked content never
// counted no matter how fast it arrived (confirmed live 2026-08-07: two
// posts stayed at ~15s LCP despite the raw HTML arriving in <0.5s).
function readPrerenderedPost(slug) {
  const data = typeof window !== "undefined" ? window.__PRERENDERED_POST__ : null;
  return data && data.slug === slug ? data : null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(() => readPrerenderedPost(slug));
  const [loading, setLoading] = useState(() => !readPrerenderedPost(slug));
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchPost = async () => {
    // Only show the spinner when we don't already have prerendered
    // content for this exact slug to paint immediately - this call still
    // always runs, to pick up any edits made since this build.
    if (!readPrerenderedPost(slug)) {
      setLoading(true);
    }
    try {
      const res = await fetch(`${API}/blog/post/${slug}`);
      if (res.ok) {
        setPost(await res.json());
      } else if (res.status === 404) {
        setError("Post not found");
      }
    } catch (err) {
      setError("Failed to load post");
    } finally {
      setLoading(false);
    }
  };

  const sharePost = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#020617] flex flex-col items-center justify-center">
        <h1 className="font-cinzel text-2xl font-bold text-[#F8FAFC] mb-4">Post Not Found</h1>
        <p className="font-manrope text-[#94A3B8] mb-6">The article you're looking for doesn't exist.</p>
        <Link to="/blog" className="text-[#D4AF37] hover:underline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.seo_title || post.title,
    "description": post.meta_description || post.excerpt,
    "image": post.og_image || post.featured_image,
    "author": {
      "@type": "Person",
      "name": post.author_name
    },
    "datePublished": post.published_at,
    "dateModified": post.updated_at || post.published_at,
    "publisher": {
      "@type": "Organization",
      "name": "Happy Ages",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": `${window.location.origin}/blog`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": window.location.href
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{post.seo_title || post.title} | Happy Ages Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta property="og:title" content={post.og_title || post.title} />
        <meta property="og:description" content={post.og_description || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.og_image || post.featured_image} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.og_title || post.title} />
        <meta name="twitter:description" content={post.og_description || post.excerpt} />
        <meta name="twitter:image" content={post.og_image || post.featured_image} />
        <link rel="canonical" href={post.canonical_url || window.location.href} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#020617]">
        <Header />

        {/* Breadcrumb */}
        <div className="pt-20 border-b border-[#D4AF37]/20">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-[#94A3B8] font-manrope">
              <Link to="/" className="hover:text-[#F8FAFC]">Home</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-[#F8FAFC]">Blog</Link>
              <span>/</span>
              <span className="text-[#D4AF37] truncate max-w-[200px]">{post.title}</span>
            </nav>
          </div>
        </div>

        {/* Article */}
        <article className="max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <header className="mb-10">
            {post.categories?.length > 0 && (
              <div className="flex gap-2 mb-4">
                {post.categories.map(cat => (
                  <Link
                    key={cat}
                    to={`/blog?category=${cat}`}
                    className="text-sm bg-[#D4AF37]/20 text-[#D4AF37] px-3 py-1 rounded-full hover:bg-[#D4AF37]/30 transition-colors"
                  >
                    {cat}
                  </Link>
                ))}
              </div>
            )}
            
            <h1 className="font-cinzel text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-6 leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-[#94A3B8] font-manrope mb-6">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author_name}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(post.published_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.reading_time} min read
              </span>
              <button
                onClick={sharePost}
                className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            {post.updated_at && post.updated_at !== post.published_at && (
              <p className="text-sm text-[#94A3B8]/70 italic font-manrope">
                Last updated: {new Date(post.updated_at).toLocaleDateString()}
              </p>
            )}
          </header>

          {/* Featured Image */}
          {post.featured_image && (
            <figure className="mb-10">
              <img
                src={optimizedImageUrl(post.featured_image, 1200)}
                alt={post.featured_image_alt || post.title}
                width="1200"
                height="630"
                fetchpriority="high"
                decoding="async"
                className="w-full h-auto rounded-2xl border border-[#D4AF37]/20 aspect-[1200/630]"
              />
              {post.featured_image_alt && (
                <figcaption className="text-center text-sm text-[#94A3B8] mt-3 font-manrope">
                  {post.featured_image_alt}
                </figcaption>
              )}
            </figure>
          )}

          {/* Table of Contents */}
          <TableOfContents content={post.content} />

          {/* Content */}
          <div className="prose prose-lg prose-invert max-w-none prose-headings:scroll-mt-20 prose-a:text-[#D4AF37] prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-p:text-[#94A3B8] prose-p:font-manrope prose-li:text-[#94A3B8] prose-strong:text-[#F8FAFC]">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                h2: ({ children }) => <HeadingRenderer level={2}>{children}</HeadingRenderer>,
                h3: ({ children }) => <HeadingRenderer level={3}>{children}</HeadingRenderer>,
                h4: ({ children }) => <HeadingRenderer level={4}>{children}</HeadingRenderer>,
                img: ({ src, alt }) => (
                  <img
                    src={src}
                    alt={alt}
                    width="800"
                    height="450"
                    loading="lazy"
                    decoding="async"
                    className="rounded-xl w-full h-auto aspect-video object-cover"
                  />
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="mt-10 pt-6 border-t border-[#D4AF37]/20 flex flex-wrap items-center gap-2">
              <Tag className="w-4 h-4 text-[#94A3B8]" />
              {post.tags.map(tag => (
                <Link
                  key={tag}
                  to={`/blog?tag=${tag}`}
                  className="text-sm bg-[#1E293B] text-[#94A3B8] px-3 py-1 rounded-full hover:bg-[#D4AF37]/20 hover:text-[#D4AF37] transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-12 card-royal p-8 md:p-12 text-center">
            <h2 className="font-cinzel text-2xl md:text-3xl font-bold text-[#D4AF37] mb-4">
              Ready to Create Your Own?
            </h2>
            <p className="font-manrope text-[#94A3B8] mb-6 max-w-lg mx-auto">
              {post.cta_service === "coat_of_arms" 
                ? "Design a custom Coat of Arms that tells your family's unique story."
                : "Create a personalized design for your special celebration."}
            </p>
            <Link
              to={post.cta_link || "/createarms"}
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#020617] px-8 py-3 rounded-lg font-cinzel font-bold text-lg hover:bg-[#B5952F] transition-colors"
            >
              {post.cta_text || "Create Your Coat of Arms"}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </article>

        {/* Related Posts */}
        {post.related_posts?.length > 0 && (
          <section className="border-t border-[#D4AF37]/20 py-12">
            <div className="max-w-6xl mx-auto px-4">
              <h2 className="font-cinzel text-2xl font-bold text-[#F8FAFC] mb-8">Related <span className="text-[#D4AF37]">Articles</span></h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {post.related_posts.map(related => (
                  <Link key={related.id} to={`/blog/${related.slug}`} className="group card-royal p-4">
                    {related.featured_image ? (
                      <img
                        src={optimizedImageUrl(related.featured_image, 800)}
                        alt={related.featured_image_alt || related.title}
                        width="400"
                        height="160"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-40 object-cover rounded-lg mb-4"
                      />
                    ) : (
                      <div className="w-full h-40 bg-[#1E293B] rounded-lg mb-4 flex items-center justify-center border border-[#D4AF37]/20">
                        <span className="text-3xl">📜</span>
                      </div>
                    )}
                    <h3 className="font-cinzel font-bold text-[#F8FAFC] group-hover:text-[#D4AF37] transition-colors">
                      {related.title}
                    </h3>
                    <p className="font-manrope text-sm text-[#94A3B8] mt-1">
                      {related.reading_time} min read
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="border-t border-[#D4AF37]/20 py-8">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-[#D4AF37] font-cinzel font-bold tracking-wider">
              <AnimatedLogo className="w-5 h-5" />
              HAPPY AGES
            </Link>
            <p className="font-manrope text-[#94A3B8] text-sm mt-2">© {new Date().getFullYear()} Happy Ages. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
